/**
 * CustomRecurrenceForm Component
 *
 * Detailed form for building custom recurrence rules.
 * Extracted from RecurrencePicker to maintain file size under 500 lines.
 *
 * Follows single responsibility principle: only handles custom recurrence input.
 */

import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';

import { Colors } from '@/constants/theme';
import type {
  RecurrenceFrequency,
  DayOfWeek,
  RecurrenceEndCondition,
} from '@/types/events';

type CustomRecurrenceFormProps = {
  frequency: RecurrenceFrequency;
  interval: string;
  byDay: DayOfWeek[];
  byMonthDay: string;
  byMonth: string;
  endCondition: RecurrenceEndCondition;
  onFrequencyChange: (freq: RecurrenceFrequency) => void;
  onIntervalChange: (value: string) => void;
  onToggleDay: (day: DayOfWeek) => void;
  onByMonthDayChange: (value: string) => void;
  onByMonthChange: (value: string) => void;
  onEndConditionChange: (condition: RecurrenceEndCondition) => void;
  onShowUntilPicker: () => void;
  palette: typeof Colors.light;
};

const DAY_OPTIONS: { value: DayOfWeek; label: string }[] = [
  { value: 'MO', label: 'Mon' },
  { value: 'TU', label: 'Tue' },
  { value: 'WE', label: 'Wed' },
  { value: 'TH', label: 'Thu' },
  { value: 'FR', label: 'Fri' },
  { value: 'SA', label: 'Sat' },
  { value: 'SU', label: 'Sun' },
];

/**
 * CustomRecurrenceForm Component
 *
 * Renders the detailed form for custom recurrence rules.
 */
export function CustomRecurrenceForm({
  frequency,
  interval,
  byDay,
  byMonthDay,
  byMonth,
  endCondition,
  onFrequencyChange,
  onIntervalChange,
  onToggleDay,
  onByMonthDayChange,
  onByMonthChange,
  onEndConditionChange,
  onShowUntilPicker,
  palette,
}: CustomRecurrenceFormProps) {
  return (
    <View style={styles.customSection}>
      <Text style={[styles.sectionLabel, { color: palette.text }]}>
        Custom Recurrence
      </Text>

      {/* Frequency selector */}
      <View style={styles.row}>
        <Text style={[styles.label, { color: palette.textMuted }]}>Repeat every</Text>
        <TextInput
          value={interval}
          onChangeText={onIntervalChange}
          keyboardType="number-pad"
          style={[
            styles.intervalInput,
            {
              borderColor: palette.border,
              color: palette.text,
              backgroundColor: palette.backgroundLight,
            },
          ]}
        />
        <View style={styles.frequencyButtons}>
          {(['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'] as RecurrenceFrequency[]).map(
            (freq) => (
              <TouchableOpacity
                key={freq}
                onPress={() => onFrequencyChange(freq)}
                style={[
                  styles.frequencyButton,
                  {
                    backgroundColor:
                      frequency === freq ? palette.tint : palette.backgroundLight,
                    borderColor: frequency === freq ? palette.tint : palette.border,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.frequencyButtonText,
                    {
                      color: frequency === freq ? '#FFF' : palette.text,
                    },
                  ]}
                >
                  {freq === 'DAILY'
                    ? 'Days'
                    : freq === 'WEEKLY'
                      ? 'Weeks'
                      : freq === 'MONTHLY'
                        ? 'Months'
                        : 'Years'}
                </Text>
              </TouchableOpacity>
            ),
          )}
        </View>
      </View>

      {/* Weekly: Day selector */}
      {frequency === 'WEEKLY' && (
        <View style={styles.daysSection}>
          <Text style={[styles.label, { color: palette.textMuted }]}>Repeat on</Text>
          <View style={styles.daysRow}>
            {DAY_OPTIONS.map(({ value, label }) => (
              <TouchableOpacity
                key={value}
                onPress={() => onToggleDay(value)}
                style={[
                  styles.dayChip,
                  {
                    backgroundColor: byDay.includes(value)
                      ? palette.tint
                      : palette.backgroundLight,
                    borderColor: byDay.includes(value) ? palette.tint : palette.border,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.dayChipText,
                    {
                      color: byDay.includes(value) ? '#FFF' : palette.text,
                    },
                  ]}
                >
                  {label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      )}

      {/* Monthly: Day of month */}
      {frequency === 'MONTHLY' && (
        <View style={styles.row}>
          <Text style={[styles.label, { color: palette.textMuted }]}>On day</Text>
          <TextInput
            value={byMonthDay}
            onChangeText={onByMonthDayChange}
            keyboardType="number-pad"
            placeholder="1-31"
            placeholderTextColor={palette.textMuted}
            style={[
              styles.dayInput,
              {
                borderColor: palette.border,
                color: palette.text,
                backgroundColor: palette.backgroundLight,
              },
            ]}
          />
          <Text style={[styles.label, { color: palette.textMuted }]}>
            of the month
          </Text>
        </View>
      )}

      {/* Yearly: Month and day */}
      {frequency === 'YEARLY' && (
        <View style={styles.row}>
          <Text style={[styles.label, { color: palette.textMuted }]}>On</Text>
          <TextInput
            value={byMonth}
            onChangeText={onByMonthChange}
            keyboardType="number-pad"
            placeholder="1-12"
            placeholderTextColor={palette.textMuted}
            style={[
              styles.dayInput,
              {
                borderColor: palette.border,
                color: palette.text,
                backgroundColor: palette.backgroundLight,
              },
            ]}
          />
          <Text style={[styles.label, { color: palette.textMuted }]}>/</Text>
          <TextInput
            value={byMonthDay}
            onChangeText={onByMonthDayChange}
            keyboardType="number-pad"
            placeholder="1-31"
            placeholderTextColor={palette.textMuted}
            style={[
              styles.dayInput,
              {
                borderColor: palette.border,
                color: palette.text,
                backgroundColor: palette.backgroundLight,
              },
            ]}
          />
          <Text style={[styles.label, { color: palette.textMuted }]}>(MM/DD)</Text>
        </View>
      )}

      {/* End condition */}
      <View style={styles.endConditionSection}>
        <Text style={[styles.label, { color: palette.textMuted }]}>Ends</Text>
        <View style={styles.endConditionButtons}>
          <TouchableOpacity
            onPress={() => onEndConditionChange({ type: 'never' })}
            style={[
              styles.endConditionButton,
              {
                backgroundColor:
                  endCondition.type === 'never' ? palette.tint : palette.backgroundLight,
                borderColor:
                  endCondition.type === 'never' ? palette.tint : palette.border,
              },
            ]}
          >
            <Text
              style={[
                styles.endConditionButtonText,
                {
                  color: endCondition.type === 'never' ? '#FFF' : palette.text,
                },
              ]}
            >
              Never
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => onEndConditionChange({ type: 'count', count: 10 })}
            style={[
              styles.endConditionButton,
              {
                backgroundColor:
                  endCondition.type === 'count' ? palette.tint : palette.backgroundLight,
                borderColor:
                  endCondition.type === 'count' ? palette.tint : palette.border,
              },
            ]}
          >
            <Text
              style={[
                styles.endConditionButtonText,
                {
                  color: endCondition.type === 'count' ? '#FFF' : palette.text,
                },
              ]}
            >
              After
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={onShowUntilPicker}
            style={[
              styles.endConditionButton,
              {
                backgroundColor:
                  endCondition.type === 'until' ? palette.tint : palette.backgroundLight,
                borderColor:
                  endCondition.type === 'until' ? palette.tint : palette.border,
              },
            ]}
          >
            <Text
              style={[
                styles.endConditionButtonText,
                {
                  color: endCondition.type === 'until' ? '#FFF' : palette.text,
                },
              ]}
            >
              On date
            </Text>
          </TouchableOpacity>
        </View>

        {endCondition.type === 'count' && (
          <View style={styles.row}>
            <Text style={[styles.label, { color: palette.textMuted }]}>
              Number of occurrences:
            </Text>
            <TextInput
              value={String(endCondition.count)}
              onChangeText={(text) => {
                const count = parseInt(text, 10);
                if (!isNaN(count) && count > 0) {
                  onEndConditionChange({ type: 'count', count });
                }
              }}
              keyboardType="number-pad"
              style={[
                styles.countInput,
                {
                  borderColor: palette.border,
                  color: palette.text,
                  backgroundColor: palette.backgroundLight,
                },
              ]}
            />
          </View>
        )}

        {endCondition.type === 'until' && (
          <View style={styles.row}>
            <Text style={[styles.label, { color: palette.textMuted }]}>Until:</Text>
            <Text style={[styles.dateText, { color: palette.text }]}>
              {endCondition.until}
            </Text>
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  customSection: {
    gap: 20,
    paddingVertical: 16,
  },
  sectionLabel: {
    fontSize: 16,
    fontWeight: '600',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  label: {
    fontSize: 14,
  },
  intervalInput: {
    width: 60,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    fontSize: 14,
    textAlign: 'center',
  },
  dayInput: {
    width: 70,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    fontSize: 14,
    textAlign: 'center',
  },
  countInput: {
    width: 80,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    fontSize: 14,
    textAlign: 'center',
  },
  frequencyButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  frequencyButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
  },
  frequencyButtonText: {
    fontSize: 12,
    fontWeight: '500',
  },
  daysSection: {
    gap: 12,
  },
  daysRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  dayChip: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
  },
  dayChipText: {
    fontSize: 12,
    fontWeight: '500',
  },
  endConditionSection: {
    gap: 12,
  },
  endConditionButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  endConditionButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: 'center',
  },
  endConditionButtonText: {
    fontSize: 12,
    fontWeight: '500',
  },
  dateText: {
    fontSize: 14,
    fontWeight: '500',
  },
});
